import React, { memo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Remarkable } from 'remarkable';
import { useTheme, useLang } from '@nbx/frontend-helpers/subscribe';
import styles from './StatusBar.module.scss';

const StatusPortal = props => {
  if (props.usePortal)
    return ReactDOM.createPortal(<StatusBar {...props} />, document.getElementById('mobile_status_hook'));
  return <StatusBar {...props} />;
};

StatusPortal.propTypes = {
  usePortal: PropTypes.bool
};

StatusPortal.defaultProps = {
  usePortal: false
};

const fetchStatus = async statusURL => {
  try {
    const res = await fetch(statusURL);
    if (!res.ok) throw new Error(`res status ${res?.status}`);
    const text = await res.text();
    return text?.trim();
  } catch (error) {
    throw new Error(`Failed to fetch status message: ${error.message}`);
  }
};

function StatusBar(props) {
  const theme = useTheme();
  const lang = useLang();
  const [htmlStatus, setHtmlStatus] = useState(null);
  const [md, setMd] = useState(null);

  useEffect(() => {
    setMd(new Remarkable());
  }, []);

  // Get status messages
  useEffect(() => {
    const getStatus = async () => {
      /* eslint-disable no-undef */
      const baseURL = __STATUS_BASE_URL__;
      try {
        let markdownStatus = '';

        if (lang && lang !== 'en') {
          markdownStatus = await fetchStatus(`${baseURL}/status_${lang}.md`);
        } else {
          markdownStatus = await fetchStatus(`${baseURL}/status.md`);
        }

        if (markdownStatus) {
          setHtmlStatus({ __html: md.render(markdownStatus) });
        }
      } catch (e) {
        console.warn('Failed to set status', e);
      }
    };

    md && getStatus();
  }, [lang, md]);

  return (
    <nbx-theme-provider theme={theme}>
      {!props.hideStatusBar && (
        <div>
          {htmlStatus && (
            <div className={styles.message}>
              {renderAlertIcon('var(--v2-antitext)')}
              <nbx-text>
                {/* eslint-disable-next-line react/no-danger */}
                <span className={styles.messageText} dangerouslySetInnerHTML={htmlStatus} />
              </nbx-text>
            </div>
          )}
        </div>
      )}
    </nbx-theme-provider>
  );
}

function renderAlertIcon(textColor) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9 12.2004C8.80222 12.2004 8.60888 12.2591 8.44443 12.369C8.27998 12.4788 8.15181 12.635 8.07612 12.8178C8.00043 13.0005 7.98063 13.2015 8.01922 13.3955C8.0578 13.5895 8.15304 13.7677 8.29289 13.9075C8.43274 14.0474 8.61093 14.1426 8.80491 14.1812C8.99889 14.2198 9.19996 14.2 9.38268 14.1243C9.56541 14.0486 9.72159 13.9205 9.83147 13.756C9.94135 13.5916 10 13.3982 10 13.2004C10 12.9352 9.89464 12.6809 9.70711 12.4933C9.51957 12.3058 9.26522 12.2004 9 12.2004Z"
        fill={textColor}
      />
      <path
        d="M9 10.2012V5.20117"
        stroke={textColor}
        strokeWidth="2"
        strokeMiterlimit="10"
        strokeLinecap="round"
      />
      <path
        d="M1 9C1 11.1217 1.84285 13.1566 3.34315 14.6569C4.84344 16.1571 6.87827 17 9 17C11.1217 17 13.1566 16.1571 14.6569 14.6569C16.1571 13.1566 17 11.1217 17 9C17 6.87827 16.1571 4.84344 14.6569 3.34315C13.1566 1.84285 11.1217 1 9 1C6.87827 1 4.84344 1.84285 3.34315 3.34315C1.84285 4.84344 1 6.87827 1 9V9Z"
        stroke={textColor}
        strokeWidth="2"
        strokeMiterlimit="10"
      />
    </svg>
  );
}

StatusBar.propTypes = {
  hideStatusBar: PropTypes.bool
};

StatusBar.defaultProps = {
  hideStatusBar: false
};

export default memo(StatusPortal);
